// src/screens/MotionScreen.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, onSnapshot, updateDoc, arrayUnion } from "firebase/firestore";
import { db, auth } from "../firebase";
import { v4 as uuidv4 } from "uuid";

function MotionScreen() {
  const { id, motionId } = useParams();
  const navigate = useNavigate();
  const [committee, setCommittee] = useState(null);
  const [loading, setLoading] = useState(true);

  // Permissions
  const [perms, setPerms] = useState({ view: false, write: false });

  // Motions
  const [motions, setMotions] = useState([]);
  const [selectedMotionId, setSelectedMotionId] = useState(motionId || null);

  // Motion data
  const [motionName, setMotionName] = useState("");
  const [raisedBy, setRaisedBy] = useState("");
  const [perSpeakerTime, setPerSpeakerTime] = useState(1);
  const [totalTime, setTotalTime] = useState(10);

  // Edit mode
  const [editMode, setEditMode] = useState(false);

  // Timer & Speakers
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [speakers, setSpeakers] = useState([]);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [elapsedSpeakers, setElapsedSpeakers] = useState([]);
  const [extraTimeMinutes, setExtraTimeMinutes] = useState(0);
  const timerRef = useRef(null);

  // Speaker edit mode
  const [speakerEditMode, setSpeakerEditMode] = useState(false);
  const [tempSpeakers, setTempSpeakers] = useState([]);

  // Load committee and motions
  useEffect(() => {
    if (!id) return;

    const unsubscribe = onSnapshot(doc(db, "committees", id), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCommittee(data);
        setMotions(data.motions || []);

        // ✅ set permissions
        if (data.createdBy === auth.currentUser?.uid) {
          setPerms({ view: true, write: true });
        } else {
          const entry = (data.accessList || []).find(
            (u) => u.email === auth.currentUser?.email
          );
          setPerms(entry?.permissions || { view: false, write: false });
        }

        if (motionId && data.motions) {
          const m = data.motions.find((x) => x.id === motionId);
          if (m) loadMotion(m);
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [id, motionId]);

  const loadMotion = (motion) => {
    setSelectedMotionId(motion.id);
    setMotionName(motion.name);
    setRaisedBy(motion.raisedBy);
    setPerSpeakerTime(motion.perSpeakerTime);
    setTotalTime(motion.totalTime);
    setSpeakers(motion.speakers || []);
    setCurrentSpeaker(motion.currentSpeaker || null);
    setTimeLeft(motion.timeLeft || 0);
    setIsRunning(motion.isRunning || false);
    setIsPaused(motion.isPaused || false);
    setIsEnded(motion.isEnded || false);
    setElapsedSpeakers(motion.elapsedSpeakers || []);
    setExtraTimeMinutes(0);
    setEditMode(false);
    setSpeakerEditMode(false);
  };

  // Helpers to update Firestore
  const updateMotionField = async (field, value) => {
    if (!perms.write) return;
    if (!selectedMotionId) return;

    try {
      const updatedMotions = motions.map((m) =>
        m.id === selectedMotionId ? { ...m, [field]: value } : m
      );
      setMotions(updatedMotions);
      await updateDoc(doc(db, "committees", id), { motions: updatedMotions });
    } catch (err) {
      console.error("Error updating motion:", err);
    }
  };

  const updateMotionFields = async (fields) => {
    if (!perms.write) return;
    if (!selectedMotionId) return;

    try {
      const updatedMotions = motions.map((m) =>
        m.id === selectedMotionId ? { ...m, ...fields } : m
      );
      setMotions(updatedMotions);
      await updateDoc(doc(db, "committees", id), { motions: updatedMotions });
    } catch (err) {
      console.error("Error updating motion:", err);
    }
  };

  // Timer effect
  useEffect(() => {
    if (!isRunning || isPaused || timeLeft <= 0 || !selectedMotionId) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;

        if (newTime <= 0) {
          if (perms.write) {
            if (currentSpeaker && !elapsedSpeakers.includes(currentSpeaker)) {
              const updatedElapsed = [...elapsedSpeakers, currentSpeaker];
              setElapsedSpeakers(updatedElapsed);

              updateMotionFields({
                elapsedSpeakers: updatedElapsed,
                isRunning: false,
                isEnded: true,
                timeLeft: 0,
                currentSpeaker: null,
                isPaused: false,
              });
            } else {
              updateMotionFields({
                isRunning: false,
                isEnded: true,
                timeLeft: 0,
                currentSpeaker: null,
                isPaused: false,
              });
            }
          }
          setCurrentSpeaker(null);
          setIsRunning(false);
          setIsEnded(true);
          setIsPaused(false);

          return 0;
        } else {
          return newTime;
        }
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRunning, isPaused, currentSpeaker, elapsedSpeakers, selectedMotionId, timeLeft, perms]);

  // Motion CRUD
  const saveMotion = async () => {
    if (!perms.write) return;
    if (!motionName.trim() || !raisedBy || !perSpeakerTime || !totalTime) {
      alert("Fill all fields.");
      return;
    }

    const newMotion = {
      id: uuidv4(),
      name: motionName,
      raisedBy,
      perSpeakerTime,
      totalTime,
      speakers: [],
      currentSpeaker: null,
      isRunning: false,
      isPaused: false,
      isEnded: false,
      timeLeft: 0,
      elapsedSpeakers: [],
    };

    try {
      await updateDoc(doc(db, "committees", id), {
        motions: arrayUnion(newMotion),
      });
      setMotionName("");
      setRaisedBy("");
      setPerSpeakerTime(1);
      setTotalTime(10);
    } catch (err) {
      console.error("Error saving motion:", err);
    }
  };

  const deleteMotion = async (motion) => {
    if (!perms.write) return;
    if (!window.confirm("Delete this motion?")) return;

    try {
      await updateDoc(doc(db, "committees", id), {
        motions: motions.filter((m) => m.id !== motion.id),
      });
      if (selectedMotionId === motion.id) setSelectedMotionId(null);
    } catch (err) {
      console.error("Error deleting motion:", err);
    }
  };

  const handleSaveEditMotion = async () => {
    if (!perms.write) return;
    if (!motionName.trim() || !raisedBy || !perSpeakerTime || !totalTime) {
      alert("Fill all fields.");
      return;
    }
    await updateMotionFields({
      name: motionName,
      raisedBy,
      perSpeakerTime,
      totalTime,
    });
    setEditMode(false);
  };

  // Speaker controls
  const startSpeaker = (country) => {
    if (!perms.write) return;
    const base = perSpeakerTime * 60;
    const extra = extraTimeMinutes * 60;
    const newTime = base + extra;

    setCurrentSpeaker(country);
    setTimeLeft(newTime);
    setIsRunning(true);
    setIsPaused(false);
    setIsEnded(false);

    updateMotionFields({
      currentSpeaker: country,
      timeLeft: newTime,
      isRunning: true,
      isPaused: false,
      isEnded: false,
    });
  };

  const handlePause = () => {
    if (!perms.write) return;
    setIsPaused(true);
    updateMotionField("isPaused", true);
  };

  const handleResume = () => {
    if (!perms.write) return;
    setIsPaused(false);
    updateMotionField("isPaused", false);
  };

  const handleNextSpeaker = () => {
    if (!perms.write) return;
    if (currentSpeaker) {
      const updatedElapsed = [...elapsedSpeakers, currentSpeaker];
      setElapsedSpeakers(updatedElapsed);
      updateMotionField("elapsedSpeakers", updatedElapsed);

      const currentIndex = speakers.indexOf(currentSpeaker);
      clearCurrentSpeaker();

      if (currentIndex < speakers.length - 1) {
        startSpeaker(speakers[currentIndex + 1]);
      }
    }
  };

  const handleEndMotion = () => {
    if (!perms.write) return;

    setIsRunning(false);
    setIsPaused(false);
    setIsEnded(true);

    if (currentSpeaker && !elapsedSpeakers.includes(currentSpeaker)) {
      const updatedElapsed = [...elapsedSpeakers, currentSpeaker];
      setElapsedSpeakers(updatedElapsed);

      updateMotionFields({
        elapsedSpeakers: updatedElapsed,
        isRunning: false,
        isPaused: false,
        isEnded: true,
        currentSpeaker: null,
      });
    } else {
      updateMotionFields({
        isRunning: false,
        isPaused: false,
        isEnded: true,
        currentSpeaker: null,
      });
    }

    setCurrentSpeaker(null);
  };

  const handleStartMotion = () => {
    if (!perms.write) return;
    if (speakers.length > 0) startSpeaker(speakers[0]);
  };

  const clearCurrentSpeaker = () => {
    if (!perms.write) return;

    setCurrentSpeaker(null);
    setTimeLeft(0);
    setIsRunning(false);
    setIsPaused(false);
    setIsEnded(false);

    updateMotionFields({
      currentSpeaker: null,
      timeLeft: 0,
      isRunning: false,
      isPaused: false,
      isEnded: false,
    });
  };

  const clearElapsedSpeakers = () => {
    if (!perms.write) return;
    if (!window.confirm("Clear elapsed speakers?")) return;

    setElapsedSpeakers([]);
    updateMotionField("elapsedSpeakers", []);
  };

  const addExtraTime = (min) => {
    if (!perms.write) return;
    if (currentSpeaker) {
      setTimeLeft((prevTimeLeft) => {
        const updatedTime = prevTimeLeft + min * 60;
        const clampedTime = Math.max(updatedTime, 0);
        updateMotionField("timeLeft", clampedTime);
        return clampedTime;
      });
    }
    setExtraTimeMinutes((prev) => {
      const updated = prev + min;
      return updated >= 0 ? updated : 0;
    });
  };

  const resetTimer = () => {
    if (!perms.write) return;
    const resetTime = perSpeakerTime * 60;
    setTimeLeft(resetTime);
    updateMotionField("timeLeft", resetTime);
  };

  const restartMotion = () => {
    if (!perms.write) return;

    setIsRunning(false);
    setIsPaused(false);
    setIsEnded(false);
    setCurrentSpeaker(null);
    setTimeLeft(0);
    setElapsedSpeakers([]);
    updateMotionFields({
      isRunning: false,
      isPaused: false,
      isEnded: false,
      currentSpeaker: null,
      timeLeft: 0,
      elapsedSpeakers: [],
    });
  };

  // Speaker Edit UI
  useEffect(() => {
    if (speakerEditMode) {
      setTempSpeakers(speakers);
    }
  }, [speakerEditMode, speakers]);

  const handleTempSpeakerToggle = (country, checked) => {
    setTempSpeakers((prev) =>
      checked ? [...prev, country] : prev.filter((c) => c !== country)
    );
  };

  const handleSaveSpeakers = () => {
    if (!perms.write) return;
    setSpeakers(tempSpeakers);
    updateMotionField("speakers", tempSpeakers);
    setSpeakerEditMode(false);
  };

  const handleClearTempSpeakers = () => {
    if (!perms.write) return;
    setTempSpeakers([]);
  };

  const restoreSpeaker = (spk) => {
    if (!perms.write) return;
    const updatedElapsed = elapsedSpeakers.filter((s) => s !== spk);
    setElapsedSpeakers(updatedElapsed);
    updateMotionField("elapsedSpeakers", updatedElapsed);
  };

  // ------------------------
  // UI Starts
  // ------------------------
  if (loading) return <div className="p-6">Loading...</div>;

  if (!committee) {
    return (
      <div className="p-6 text-center text-red-500">
        Committee not found
        <button
          onClick={() => navigate("/main")}
          className="mt-4 bg-gray-600 text-white px-4 py-2 rounded-lg"
        >
          Back
        </button>
      </div>
    );
  }
  // ✅ Attendance calculation goes HERE (before return)
  const countries = committee.countries || {};
  const totalCountries = Object.keys(countries).length;

  // Treat both "Present" and "Present and Voting" as present
  const presentCountries = Object.entries(countries).filter(
    ([, status]) => status.toLowerCase().includes("present")
  ).length;

  const simpleMajority = Math.floor(presentCountries / 2) + 1;

  // Inside the main return JSX of MotionScreen, replacing the existing UI rendering part

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Motion Management – {committee.name}
        </h1>
        <button
          onClick={() => navigate("/main")}
          className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition"
        >
          Back to Dashboard
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-4 mb-6 border border-gray-100 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
          Attendance Summary
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          Total Countries: <b>{totalCountries}</b>
        </p>
        <p className="text-gray-700 dark:text-gray-300">
          Present (including Present & Voting): <b>{presentCountries}</b>
        </p>
        <p className="text-gray-700 dark:text-gray-300">
          Simple Majority: <b>{simpleMajority}</b>
        </p>
      </div>

      {!selectedMotionId ? (
        <>
          {/* Add Motion (Write Only) */}
          {perms.write && (
            <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6 border border-gray-100 dark:border-gray-700">
              <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">
                Add Motion
              </h2>
              <input
                type="text"
                value={motionName}
                onChange={(e) => setMotionName(e.target.value)}
                placeholder="Enter motion..."
                className="border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-full mb-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
              />
              <div className="mb-3">
                <label className="block mb-1 text-gray-700 dark:text-gray-200">
                  Raised by:
                </label>
                {!raisedBy ? (
                  <div className="flex flex-wrap gap-2">
                    {committee.countries &&
                      Object.keys(committee.countries).map((country) => (
                        <button
                          key={country}
                          onClick={() => setRaisedBy(country)}
                          className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          {country}
                        </button>
                      ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-2 bg-blue-600 text-white rounded-lg">
                      {raisedBy}
                    </span>
                    {perms.write && (
                      <button
                        onClick={() => setRaisedBy("")}
                        className="text-red-600 dark:text-red-400 text-sm"
                      >
                        Change
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-4 mb-3">
                <div>
                  <label className="block text-gray-700 dark:text-gray-200">
                    Per Speaker Time (min)
                  </label>
                  <input
                    type="number"
                    value={perSpeakerTime}
                    onChange={(e) => setPerSpeakerTime(Number(e.target.value))}
                    className="border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-28 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 dark:text-gray-200">
                    Total Time (min)
                  </label>
                  <input
                    type="number"
                    value={totalTime}
                    onChange={(e) => setTotalTime(Number(e.target.value))}
                    className="border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-28 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  />
                </div>
              </div>

              <button
                onClick={saveMotion}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
              >
                Save Motion
              </button>
            </div>
          )}

          {/* Motion List */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(motions || []).map((motion) => (
              <div
                key={motion.id}
                className="bg-white dark:bg-gray-800 shadow rounded-xl p-4 border border-gray-100 dark:border-gray-700"
              >
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {motion.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Raised by: {motion.raisedBy}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Speaker: {motion.perSpeakerTime}min | Total: {motion.totalTime}min
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => {
                      loadMotion(motion);
                      navigate(`/committee/${id}/motion/${motion.id}`);
                    }}
                    className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-700 transition"
                  >
                    Manage
                  </button>
                  {perms.write && (
                    <button
                      onClick={() => deleteMotion(motion)}
                      className="bg-red-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}

            {motions.length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 italic">No motions yet.</p>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Back */}
          <button
            onClick={() => {
              setSelectedMotionId(null);
              navigate(`/committee/${id}`);
            }}
            className="bg-gray-500 text-white px-3 py-1 rounded-lg mb-4 hover:bg-gray-600 transition"
          >
            ← Back to Motions
          </button>

          {/* Motion Details */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6 border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Motion Details
              </h2>
              {perms.write && (
                !editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded-lg hover:bg-yellow-600 transition"
                  >
                    Edit
                  </button>
                ) : (
                  <button
                    onClick={handleSaveEditMotion}
                    className="bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700 transition"
                  >
                    Save
                  </button>
                )
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Name</label>
                <input
                  type="text"
                  value={motionName}
                  onChange={(e) => setMotionName(e.target.value)}
                  disabled={!editMode || !perms.write}
                  className={`border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-full ${!editMode
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                    }`}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Raised By</label>
                {editMode && perms.write ? (
                  <select
                    value={raisedBy}
                    onChange={(e) => setRaisedBy(e.target.value)}
                    className="border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-full bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                  >
                    <option value="">Select country</option>
                    {committee.countries &&
                      Object.keys(committee.countries).map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={raisedBy}
                    disabled
                    className="border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Per Speaker Time (min)</label>
                <input
                  type="number"
                  value={perSpeakerTime}
                  onChange={(e) => setPerSpeakerTime(Number(e.target.value))}
                  disabled={!editMode || !perms.write}
                  className={`border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-full ${!editMode
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                    }`}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Total Time (min)</label>
                <input
                  type="number"
                  value={totalTime}
                  onChange={(e) => setTotalTime(Number(e.target.value))}
                  disabled={!editMode || !perms.write}
                  className={`border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-lg w-full ${!editMode
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
                    }`}
                />
              </div>
            </div>
          </div>

          {/* Speakers List */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold">Speakers List</h2>
              {perms.write && !speakerEditMode && (
                <button
                  onClick={() => setSpeakerEditMode(true)}
                  className="bg-yellow-500 text-white px-3 py-1 rounded-lg"
                >
                  Edit
                </button>
              )}
            </div>

            {speakerEditMode && perms.write && (
              <div className="mb-3">
                <h3 className="text-sm font-semibold mb-2 text-gray-100">Select speakers (order of selection is preserved):</h3>
                <div className="flex flex-wrap gap-2">
                  {committee.countries &&
                    Object.keys(committee.countries).map((country) => (
                      <label
                        key={country}
                        className="flex items-center gap-1 bg-gray-800 px-3 py-1 rounded cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={tempSpeakers.includes(country)}
                          onChange={(e) => handleTempSpeakerToggle(country, e.target.checked)}
                          className="accent-blue-500"
                        />
                        <span className="text-gray-100">{country}</span>
                      </label>
                    ))}
                </div>
                <button
                  onClick={handleSaveSpeakers}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg mt-2 hover:bg-green-700 transition"
                >
                  OK
                </button>
                <button
                  onClick={handleClearTempSpeakers}
                  className="bg-red-600 text-white px-3 py-2 rounded-lg mt-2 hover:bg-red-700 transition"
                >
                  Clear All
                </button>
              </div>
            )}

            <div>
              {speakers.length > 0 ? (
                <ul className="list-disc pl-6">
                  {speakers.map((spk) => (
                    <li key={spk} className="flex items-center gap-2">
                      <span>
                        {elapsedSpeakers.includes(spk) && (
                          <span className="text-red-600 mr-2">[Elapsed]</span>
                        )}
                        {spk}
                      </span>
                      {currentSpeaker === spk ? (
                        <span className="text-green-600 ml-2">(Current)</span>
                      ) : !currentSpeaker && perms.write ? (
                        <button
                          onClick={() => startSpeaker(spk)}
                          className="ml-2 text-blue-600 underline"
                        >
                          Start
                        </button>
                      ) : (
                        <span className="ml-2 text-gray-400">(Waiting)</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 italic">No speakers.</p>
              )}
            </div>
          </div>

          {/* Elapsed Speakers */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold dark:text-gray-100">Elapsed Speakers</h2>
              {perms.write && (
                <button
                  onClick={clearElapsedSpeakers}
                  className="bg-red-600 text-white px-3 py-1 rounded-lg"
                >
                  Clear
                </button>
              )}
            </div>
            {elapsedSpeakers.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {elapsedSpeakers.map((spk) => (
                  <span
                    key={spk}
                    className="px-3 py-1 rounded-lg flex items-center gap-2 bg-gray-700 text-gray-100"
                  >
                    {spk}
                    {perms.write && (
                      <button
                        onClick={() => restoreSpeaker(spk)}
                        className="text-blue-400 underline text-xs"
                      >
                        Restore
                      </button>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic dark:text-gray-400">None yet.</p>
            )}
          </div>

          {/* Timer */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold mb-3">Timer</h2>
            {currentSpeaker ? (
              <div className="text-center">
                <p className="text-lg mb-2">
                  Speaking: <b>{currentSpeaker}</b>
                </p>
                <p
                  className={`text-3xl font-bold ${timeLeft <= 10 ? "text-red-600" : ""
                    }`}
                >
                  {Math.floor(timeLeft / 60)}:
                  {(timeLeft % 60).toString().padStart(2, "0")}
                </p>
                {perms.write && (
                  <>
                    <div className="flex justify-center gap-2 mt-4">
                      <button
                        onClick={() => addExtraTime(1)}
                        className="bg-blue-200 text-blue-800 px-2 py-1 rounded-lg"
                      >
                        +1 min
                      </button>
                      <button
                        onClick={() => addExtraTime(-1)}
                        disabled={timeLeft <= 60}
                        className="bg-blue-200 text-blue-800 px-2 py-1 rounded-lg"
                      >
                        -1 min
                      </button>
                      <button
                        onClick={resetTimer}
                        className="bg-gray-200 text-gray-800 px-2 py-1 rounded-lg"
                      >
                        Reset Timer
                      </button>
                    </div>
                    <div className="flex justify-center gap-4 mt-4">
                      {!isPaused ? (
                        <button
                          onClick={handlePause}
                          className="bg-yellow-500 text-white px-4 py-2 rounded-lg"
                        >
                          Pause
                        </button>
                      ) : (
                        <button
                          onClick={handleResume}
                          className="bg-green-500 text-white px-4 py-2 rounded-lg"
                        >
                          Resume
                        </button>
                      )}
                      <button
                        onClick={handleNextSpeaker}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                      >
                        Next
                      </button>
                      <button
                        onClick={handleEndMotion}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg"
                      >
                        End
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-center text-gray-500">No active speaker.</p>
            )}
          </div>

          {/* Motion Status & Controls */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold mb-3">Motion Status</h2>
            <div className="flex gap-4 items-center">
              <span>
                Status:{" "}
                <span
                  className={`font-bold ${isEnded
                    ? "text-red-600"
                    : isPaused
                      ? "text-yellow-600"
                      : isRunning
                        ? "text-green-600"
                        : "text-gray-600"
                    }`}
                >
                  {isEnded
                    ? "Ended"
                    : isPaused
                      ? "Paused"
                      : isRunning
                        ? "Active"
                        : "Idle"}
                </span>
              </span>
              {perms.write && (
                <>
                  <button
                    onClick={restartMotion}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg"
                  >
                    Reset Motion
                  </button>
                  <button
                    onClick={handleStartMotion}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Start Motion
                  </button>
                  <button
                    onClick={clearCurrentSpeaker}
                    className="bg-gray-400 text-white px-4 py-2 rounded-lg"
                  >
                    Clear Speaker
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MotionScreen;